import React, { useEffect, useState } from 'react'
import { Users } from 'lucide-react'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { matchingService } from '@/services/matchingService'
import { useAuth } from '@/contexts/Auth0Context'
import { Relationship, RelationshipStatus } from '@/types/matching'
import { PostVisibility } from '@/types/feed'

interface FeedSidebarProps {
  visibility: PostVisibility | undefined
  onChange: (visibility: PostVisibility | undefined) => void
}

const relationshipName = (relationship: Relationship, currentUserId: string | undefined): string => {
  if (relationship.mentorId === currentUserId) {
    return relationship.mentee
      ? `${relationship.mentee.firstName} ${relationship.mentee.lastName}`
      : 'Mentee'
  }
  return relationship.mentor
    ? `${relationship.mentor.firstName} ${relationship.mentor.lastName}`
    : 'Mentor'
}

const FeedSidebar: React.FC<FeedSidebarProps> = ({ visibility, onChange }) => {
  const { user } = useAuth()
  const [relationships, setRelationships] = useState<Relationship[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      try {
        const data = await matchingService.getMyRelationships()
        const active = (Array.isArray(data) ? data : []).filter(
          (r) => r.status === RelationshipStatus.ACTIVE
        )
        if (!cancelled) setRelationships(active)
      } catch {
        if (!cancelled) setRelationships([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Your Connections</h2>
          </div>
        </CardHeader>
        <CardBody>
          {loading && (
            <div className="space-y-2 animate-pulse">
              <div className="h-10 bg-gray-100 rounded-md" />
            </div>
          )}

          {!loading && relationships.length === 0 && (
            <p className="text-gray-600 text-sm py-2 text-center">
              You don't have an active mentorship connection yet.
            </p>
          )}

          {!loading && relationships.length > 0 && (
            <ul className="space-y-2">
              {relationships.map((relationship) => (
                <li key={relationship.id} className="text-sm font-medium text-gray-900">
                  {relationshipName(relationship, user?.id)}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold text-gray-900">Visibility</h2>
        </CardHeader>
        <CardBody>
          <select
            aria-label="Filter feed by visibility"
            value={visibility ?? ''}
            onChange={(e) => onChange((e.target.value || undefined) as PostVisibility | undefined)}
            className="w-full rounded-md border border-gray-200 px-2 py-1 text-sm focus:border-primary-500 focus:outline-none"
          >
            <option value="">All</option>
            <option value={PostVisibility.PUBLIC}>Public</option>
            <option value={PostVisibility.CONNECTIONS}>Connections</option>
          </select>
        </CardBody>
      </Card>
    </div>
  )
}

export default FeedSidebar
